import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ExclamationTriangleIcon, ChevronRight } from '@/lib/icons'
import { Switch } from "@/components/ui/switch";
import { DatePicker } from "@/components/ui/date-picker";
import FacilitatorLayout from '@/components/FacilitatorLayout';
import { Header } from '@/components/prosci/Header';
import { SearchableSelect } from '@/components/SearchableSelect'

// Note: This component replicates the basic Rails form structure.
// For a true React-driven form, consider using libraries like Formik or React Hook Form
// along with client-side API calls instead of relying on the Rails form builder postback.

const FacilitatorAssessmentSessionsNew = ({
  formAction,
  formMethod,
  csrfToken,
  errors,
  assessmentSession,
  existingProjects = [], // Default to empty array
  backUrl,
  submitLabel,
  currentUser
}) => {
  // State for controlled components
  const [selectedProjectId, setSelectedProjectId] = useState('')
  const [newProjectName, setNewProjectName] = useState('')
  const [showNewProjectInput, setShowNewProjectInput] = useState(false)
  const [sessionName, setSessionName] = useState(assessmentSession?.name || '')
  const [assessmentType, setAssessmentType] = useState(assessmentSession?.assessmentType || 'quick_interactive')
  const [expiresAt, setExpiresAt] = useState(assessmentSession?.expiresAt ? assessmentSession.expiresAt.slice(0, 10) : '')
  const [active, setActive] = useState(assessmentSession?.active !== false)

  // Timer settings state
  const [timerEnabled, setTimerEnabled] = useState(assessmentSession?.timerEnabled || false)
  const [timerDurationSeconds, setTimerDurationSeconds] = useState(assessmentSession?.timerDurationSeconds?.toString() || '30')
  const [timerDefaultAction, setTimerDefaultAction] = useState(assessmentSession?.timerDefaultAction || 'opportunity')

  const assessmentTypeOptions = [
    { label: 'Initial', value: 'quick_interactive' },
    { label: 'Detailed', value: 'full_detailed' },
  ]

  // Prepare project options for SearchableSelect
  const projectOptions = existingProjects.map((project) => ({
    value: project.id.toString(),
    label: project.name,
  }))

  // Initialize project selection state based on props and errors
  useEffect(() => {
    const initialProjectId = assessmentSession?.projectId
    const initialNewProjectName = assessmentSession?.newProjectName || ''
    let defaultSelectedId = ''
    let shouldShowNew = false

    if (errors?.length > 0 && initialProjectId === 'new') {
      // Error state with 'new' selected
      shouldShowNew = true
      defaultSelectedId = 'new'
    } else if (initialProjectId && initialProjectId !== 'new') {
      // Error state or initial load with an existing project ID
      defaultSelectedId = initialProjectId.toString()
    } else if (existingProjects.length > 0) {
      // Default to first existing project if available
      defaultSelectedId = existingProjects[0].id.toString()
    } else {
      // No existing projects, default to 'new'
      shouldShowNew = true
      defaultSelectedId = 'new'
    }

    setSelectedProjectId(defaultSelectedId)
    setShowNewProjectInput(shouldShowNew)
    setNewProjectName(initialNewProjectName) // Persist potentially errored new name
    setSessionName(assessmentSession?.name || '') // Persist session name
    setAssessmentType(assessmentSession?.assessmentType || assessmentTypeOptions[0].value)
    setExpiresAt(assessmentSession?.expiresAt ? assessmentSession.expiresAt.slice(0, 10) : '')

    // Only update if editing an existing session (has an id or similar unique property)
    if (assessmentSession && typeof assessmentSession.active === 'boolean') {
      setActive(assessmentSession.active)
    }
    // Timer settings initialization
    setTimerEnabled(assessmentSession?.timerEnabled || false)
    setTimerDurationSeconds(assessmentSession?.timerDurationSeconds?.toString() || '30')
    setTimerDefaultAction(assessmentSession?.timerDefaultAction || 'opportunity')

    // Otherwise, do not override the default
    // eslint-disable-next-line
  }, [assessmentSession, errors, existingProjects])

  const handleProjectChange = (value) => {
    setSelectedProjectId(value)
    if (value === 'new') {
      setShowNewProjectInput(true)
    } else {
      setShowNewProjectInput(false)
      setNewProjectName('')
    }
  }

  // Find specific errors for fields
  const projectErrors = errors?.filter((e) => e.toLowerCase().includes('project id') || e.toLowerCase().includes('selected project')) || []
  const newProjectNameErrors = errors?.filter((e) => e.toLowerCase().includes('new project name')) || []
  const otherErrors = errors?.filter((e) => !e.toLowerCase().includes('project id') && !e.toLowerCase().includes('selected project') && !e.toLowerCase().includes('new project name')) || []

  return (
    <FacilitatorLayout userName={currentUser?.name || 'Facilitator'}>
      <Header
        title={`${assessmentSession?.id ? 'Edit' : 'Create New'} Assessment Session`}
        size="small"
        breadcrumb={
          <nav className="flex items-center gap-1.5 text-sm">
            <a href={backUrl}>Sessions</a>
            <ChevronRight className="h-3 w-3" />
            <span>{assessmentSession?.id ? 'Edit Session' : 'New Session'}</span>
          </nav>
        }
      />
      <div className="container mx-auto p-4 md:p-6 max-w-[800px]">
        {errors.length > 0 && (
          <Alert variant="destructive" className="mb-6">
            <ExclamationTriangleIcon className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              <ul className="list-disc pl-5">
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Assessment Session Details</CardTitle>
          </CardHeader>
          <CardContent>
            <form action={formAction} method="post" acceptCharset="UTF-8" id="new-session-form">
              {formMethod !== 'get' && <input type="hidden" name="_method" value={formMethod} />}
              <input type="hidden" name="authenticity_token" value={csrfToken} />

              <div className="space-y-4">
                {/* Session Name Input */}
                <div className="space-y-1">
                  <Label htmlFor="assessment_session_name">Session Name (Optional)</Label>
                  <Input id="assessment_session_name" name="assessment_session[name]" value={sessionName} onChange={(e) => setSessionName(e.target.value)} placeholder="e.g., Q2 Workshop Group A" />
                </div>

                {/* Project Selection/Creation */}
                <div className="space-y-1">
                  <Label htmlFor="assessment_session_project_id">Project</Label>
                  <input type="hidden" id="assessment_session_project_id" name="assessment_session[project_id]" value={selectedProjectId} />
                  <SearchableSelect value={selectedProjectId} onValueChange={handleProjectChange} options={projectOptions} placeholder="Select a project..." searchPlaceholder="Search projects..." emptyText="No projects found." className={projectErrors.length > 0 ? 'border-red-500' : ''} showCreateNew={true} createNewText="-- Create New Project --" />
                  {projectErrors.map((msg, i) => (
                    <p key={i} className="text-xs text-red-600 mt-1">
                      {msg}
                    </p>
                  ))}

                  {showNewProjectInput && (
                    <div className="space-y-1 pt-2">
                      <Label htmlFor="assessment_session_new_project_name">New Project Name</Label>
                      <Input id="assessment_session_new_project_name" name="assessment_session[new_project_name]" value={newProjectName} onChange={(e) => setNewProjectName(e.target.value)} placeholder="Enter name for the new project" className={newProjectNameErrors.length > 0 ? 'border-red-500' : ''} />
                      {newProjectNameErrors.map((msg, i) => (
                        <p key={i} className="text-xs text-red-600 mt-1">
                          {msg}
                        </p>
                      ))}
                    </div>
                  )}
                </div>

                {/* Assessment Type Selection */}
                <div className="space-y-1">
                  <Label htmlFor="assessment_session_assessment_type">Assessment Type</Label>
                  <Select name="assessment_session[assessment_type]" value={assessmentType} onValueChange={setAssessmentType}>
                    <SelectTrigger id="assessment_session_assessment_type">
                      <SelectValue placeholder="Select type..." />
                    </SelectTrigger>
                    <SelectContent>
                      {assessmentTypeOptions.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Timer Configuration - Conditional based on assessmentType */}
                {assessmentType === 'quick_interactive' && (
                  <div className="space-y-4 p-4 border rounded-md mt-4">
                    <h3 className="text-xl font-medium">Timer Settings</h3>
                    <div className="flex items-center space-x-2">
                      <Switch id="assessment_session_timer_enabled" checked={timerEnabled} onCheckedChange={setTimerEnabled} />
                      <Label htmlFor="assessment_session_timer_enabled">Enable Timer per Best Practice</Label>
                      <input type="hidden" name="assessment_session[timer_enabled]" value={timerEnabled ? 'true' : 'false'} />
                    </div>

                    {timerEnabled && (
                      <>
                        <div className="space-y-1">
                          <Label htmlFor="assessment_session_timer_duration_seconds">Time per Best Practice (seconds)</Label>
                          <Input id="assessment_session_timer_duration_seconds" name="assessment_session[timer_duration_seconds]" type="number" value={timerDurationSeconds} onChange={(e) => setTimerDurationSeconds(e.target.value)} placeholder="e.g., 30" min="1" />
                        </div>

                        <div className="space-y-1">
                          <Label htmlFor="assessment_session_timer_default_action">Action if time expires</Label>
                          <Select name="assessment_session[timer_default_action]" value={timerDefaultAction} onValueChange={setTimerDefaultAction}>
                            <SelectTrigger id="assessment_session_timer_default_action">
                              <SelectValue placeholder="Select default action..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="opportunity">Record as Opportunity</SelectItem>
                              <SelectItem value="strength">Record as Strength</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Expiration Date Input */}
                <div className="space-y-1">
                  <Label htmlFor="assessment_session_expires_at">Expiration Date (optional)</Label>
                  <input type="hidden" id="assessment_session_expires_at" name="assessment_session[expires_at]" value={expiresAt} />
                  <DatePicker value={expiresAt} onChange={setExpiresAt} placeholder="Select expiration date" />
                </div>

                {/* Active Toggle */}
                <div className="flex items-center space-x-2">
                  <Switch id="assessment_session_active" name="assessment_session_active_visual" checked={active} onCheckedChange={setActive} />
                  <Label htmlFor="assessment_session_active">Active</Label>
                  <input type="hidden" name="assessment_session[active]" value={active ? 'true' : 'false'} />
                </div>
              </div>
            </form>
          </CardContent>
          <CardFooter className="flex justify-end gap-2">
            {/* Action Buttons */}
            <Button variant="ghost" asChild>
              <a href={backUrl}>Cancel</a>
            </Button>
            <Button type="submit" form="new-session-form">
              {submitLabel || 'Create Session'}
            </Button>
          </CardFooter>
        </Card>
      </div>
    </FacilitatorLayout>
  )
};

FacilitatorAssessmentSessionsNew.propTypes = {
  formAction: PropTypes.string.isRequired,
  formMethod: PropTypes.string.isRequired,
  csrfToken: PropTypes.string.isRequired,
  errors: PropTypes.array,
  assessmentSession: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    projectId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    assessmentType: PropTypes.string,
    active: PropTypes.bool,
    expiresAt: PropTypes.string
  }),
  existingProjects: PropTypes.array,
  backUrl: PropTypes.string.isRequired,
  submitLabel: PropTypes.string,
  currentUser: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string
  })
};

FacilitatorAssessmentSessionsNew.defaultProps = {
  errors: [],
  assessmentSession: {},
  submitLabel: 'Create Session'
};

export default FacilitatorAssessmentSessionsNew; 